import { Box, Flex, Grid } from '@chakra-ui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Icon } from '@main/complykit/components/icon';
import { Skeleton } from '@main/complykit/components/skeleton';
import { Text } from '@main/complykit/components/text';
import type { Table as TanstackTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { EmptyPlaceholder } from '../placeholder';
import { selectionColumn } from './bulk-actions';
import { TableItem } from './table';
import { actionsColumnId, isInternalTableColumnId, selectionColumnId } from './table-column-ids';
import { columnTrack } from './table-grid';

/* The real header names over five rows at the row's step, announced once, on
 * the loaded table's own column template: saved and fixed widths hold, and
 * the internal columns keep their tracks, empty. The selection column only
 * exists once two selectable rows arrive, so a table that can select rows
 * reserves it while loading. */
export const TableLoadingState = <TData,>({
  table,
  minColumnSize,
  isReorderable,
  reservesSelection,
}: {
  table: TanstackTable<TData>;
  minColumnSize: number;
  isReorderable: boolean;
  reservesSelection: boolean;
}) => {
  const { t } = useTranslation('ui');
  const headers = table.getFlatHeaders();
  const columnSizing = table.getState().columnSizing;
  const known = headers.map((header) => ({
    id: header.id,
    track: columnTrack(header, { columnSizing, minColumnSize, isReorderable }),
    isInternal: isInternalTableColumnId(header.column.id),
    isActions: header.column.id === actionsColumnId,
    /* The engine's own rule: an edge-pinned column spans into an implicit track. */
    span: header.column.getIsFirstColumn('left') || header.column.getIsLastColumn('right') ? 2 : 1,
    name: header.column.columnDef.header,
  }));
  const hasSelection = headers.some((header) => header.column.id === selectionColumnId);
  const reserved =
    reservesSelection && !hasSelection
      ? [
          {
            id: selectionColumnId,
            track: `${selectionColumn.size}px`,
            isInternal: true,
            isActions: false,
            span: 1,
            name: undefined,
          },
        ]
      : [];
  const cells =
    known.length > 0
      ? [...known.slice(0, 1), ...reserved, ...known.slice(1)]
      : Array.from({ length: 4 }, (_, index) => ({
          id: String(index),
          track: 'minmax(0, 1fr)',
          isInternal: false,
          isActions: false,
          span: 1,
          name: undefined,
        }));
  /* The loaded rows sit on `grid-column: 1 / end`, and the unnamed `end` line
   * adds one implicit auto track after the declared ones; the edge-pinned
   * actions cell spans into it. The trailing auto is that track. */
  const templateColumns = `${cells.map((cell) => cell.track).join(' ')} auto`;
  /* All names or all bars: half of each reads as broken, and custom field
   * columns render their header as a function. */
  const hasNames = cells
    .filter((cell) => !cell.isInternal)
    .every((cell) => typeof cell.name === 'string');
  return (
    <Skeleton.Region label={t('loading.table')}>
      <Box py={2}>
        <Grid templateColumns={templateColumns}>
          {cells.map((cell) =>
            cell.isInternal ? (
              <InternalCell key={cell.id} span={cell.span} isActions={cell.isActions} />
            ) : (
              <Box key={cell.id} px={2} py={3} minW={0} gridColumnEnd={`span ${cell.span}`}>
                {hasNames ? (
                  <Text size="xs" weight="medium" color="muted" isTruncated>
                    {cell.name as string}
                  </Text>
                ) : (
                  <Skeleton.Text size="xs" />
                )}
              </Box>
            ),
          )}
        </Grid>
        {Array.from({ length: 5 }, (_, row) => (
          <Grid
            key={row}
            templateColumns={templateColumns}
            borderTop="1px solid"
            borderColor="var(--ck-border-divider)"
          >
            {cells.map((cell) =>
              cell.isInternal ? (
                <InternalCell key={cell.id} span={cell.span} isActions={cell.isActions} />
              ) : (
                <Box key={cell.id} px={2} py={3} minW={0} gridColumnEnd={`span ${cell.span}`}>
                  <Skeleton.Text size="sm" />
                </Box>
              ),
            )}
          </Grid>
        ))}
      </Box>
    </Skeleton.Region>
  );
};

/* An internal column's track, empty. The actions column keeps the actions
 * cell's own box (12px either side of a 32px icon button) so its implicit
 * track is as wide as the loaded one. */
const InternalCell = ({ span, isActions }: { span: number; isActions: boolean }) => (
  <Box gridColumnEnd={`span ${span}`}>{isActions ? <Box w={14} /> : null}</Box>
);

export const TableEmptyState = ({
  itemName,
  subHeading,
}: {
  itemName: TableItem;
  subHeading?: string;
}) => {
  const { t } = useTranslation('ui');

  return (
    <EmptyPlaceholder py={8}>
      <EmptyPlaceholder.Icon as={XMarkIcon} />
      <EmptyPlaceholder.Content>
        <EmptyPlaceholder.Heading>
          {t('table.empty.heading', { item: itemName.plural })}
        </EmptyPlaceholder.Heading>
        {subHeading && <EmptyPlaceholder.Subheading>{subHeading}</EmptyPlaceholder.Subheading>}
      </EmptyPlaceholder.Content>
    </EmptyPlaceholder>
  );
};

export const TableEmptyFilterResult = ({ itemName }: { itemName: TableItem }) => {
  const { t } = useTranslation('ui');

  return (
    <Flex direction="column" alignItems="center" py={8} gap={1} maxW="320px" mx="auto">
      <Flex
        w={12}
        h={12}
        justify="center"
        alignItems="center"
        bgColor="var(--ck-accent-a-2)"
        rounded="full"
        mb={3}
      >
        {/* The rings carry the brand tint and the glyph inherits it, so the
         * whole pictogram follows an accent change as one thing. */}
        <Flex
          w={9}
          h={9}
          justify="center"
          alignItems="center"
          bgColor="var(--ck-accent-a-3)"
          rounded="full"
          color="var(--ck-accent-11)"
        >
          <Icon as={MagnifyingGlassIcon} size="xl" />
        </Flex>
      </Flex>
      <Text size="base" weight="semibold">
        {`${t('table.filter.notFoundHeading', { item: itemName.plural })}`}
      </Text>
      <Text as="span" size="sm" color="muted" align="center">
        {`${t('table.filter.notFoundSubheadingFirstLine', { item: itemName.singular })}`}
      </Text>
      <Text as="span" size="sm" color="muted" align="center">
        {`${t('table.filter.notFoundSubheadingSecondLine', { item: itemName.singular })}`}
      </Text>
    </Flex>
  );
};
